import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, AlertCircle, Eye, Filter, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import StaffPageHeader from '../../components/StaffPageHeader';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;

const statusClass = (v) => {
  const key = String(v || '').toLowerCase();
  if (['approved', 'sent', 'converted'].includes(key)) return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200';
  if (['rejected', 'failed', 'cancelled', 'blocked'].includes(key)) return 'border-red-500/40 bg-red-500/10 text-red-200';
  if (['pending', 'pending_approval', 'draft', 'reviewed', 'queued'].includes(key)) return 'border-amber-500/40 bg-amber-500/10 text-amber-200';
  return 'border-slate-500/40 bg-slate-600/10 text-slate-200';
};

const priorityClass = (v) => {
  const key = String(v || '').toLowerCase();
  if (key === 'critical') return 'border-red-500/45 bg-red-500/15 text-red-200';
  if (key === 'high') return 'border-amber-500/45 bg-amber-500/15 text-amber-200';
  if (key === 'medium') return 'border-cyan-500/45 bg-cyan-500/15 text-cyan-200';
  return 'border-slate-500/40 bg-slate-600/10 text-slate-200';
};

const fmt = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleString('id-ID', { hour12: false });
};

const Badge = ({ children, cls }) => <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{children}</span>;

const StatCard = ({ label, value }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0b1222] p-4">
    <p className="text-[11px] uppercase tracking-[0.08em] text-slate-400">{label}</p>
    <p className="mt-1 text-2xl font-bold text-white">{value}</p>
  </div>
);

const EmptyState = ({ title, subtitle }) => (
  <div className="rounded-xl border border-dashed border-white/20 bg-[#0b1222]/70 p-5 text-center">
    <p className="text-sm font-semibold text-white">{title}</p>
    <p className="mt-1 text-xs text-slate-400">{subtitle}</p>
  </div>
);

const AdminAIControlCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [suppressedPredictions, setSuppressedPredictions] = useState([]);
  const [incidentClusters, setIncidentClusters] = useState([]);
  const [clusterPriorityQueue, setClusterPriorityQueue] = useState([]);
  const [clusterSummary, setClusterSummary] = useState({
    open_clusters: 0,
    acknowledged_clusters: 0,
    critical_clusters: 0,
    high_clusters: 0,
  });
  const [waDrafts, setWaDrafts] = useState([]);
  const [ticketDrafts, setTicketDrafts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [itemType, setItemType] = useState('all');
  const [status, setStatus] = useState('all');
  const [priority, setPriority] = useState('all');
  const [selectedItemId, setSelectedItemId] = useState('');

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const mapPred = useCallback((r) => ({
    id: `pred-${r.id}`,
    refId: r.id,
    type: 'Prediksi AI',
    source: 'Prediksi',
    title: r.summary || `Prediksi ${r.prediction_type}`,
    summary: r.summary || '-',
    explanation: r.explanation || '-',
    priority: r.risk_level || 'low',
    confidence: Number(r.confidence || 0),
    status: r.status || 'new',
    createdAt: r.detected_at || r.created_at,
    canApprove: false,
    canReject: false,
    groupedByCluster: Boolean(r.grouped_by_cluster),
    incidentClusterId: r.incident_cluster_id || null,
    isCluster: false,
  }), []);

  const mapClusterCard = useCallback((r) => ({
    id: `cluster-${r.cluster_id}`,
    refId: r.cluster_id,
    type: 'Incident Cluster',
    source: 'Cluster',
    title: r.title || `Cluster ${r.cluster_type || 'insiden'}`,
    summary: r.summary || '-',
    explanation: r.probable_root_cause || '-',
    priority: r.severity || 'medium',
    confidence: Number(r.confidence || 0),
    status: r.status || 'open',
    createdAt: r.first_detected_at || r.created_at,
    canApprove: false,
    canReject: false,
    groupedByCluster: true,
    incidentClusterId: r.cluster_id,
    isCluster: true,
    affectedCustomersCount: Number(r.affected_customers_count || 0),
    sourceEventsCount: Number(r.source_events_count || 0),
    memberBreakdown: r.member_breakdown || {},
    recommendedActions: Array.isArray(r.recommended_actions) ? r.recommended_actions : [],
  }), []);

  const mapWa = useCallback((r) => ({
    id: `wa-${r.id}`,
    refId: r.id,
    type: 'Draf WA Proaktif',
    source: 'WhatsApp',
    title: r.template_key || r.message_type || 'Draf WA',
    summary: r.rendered_message || '-',
    explanation: `Template: ${r.template_key || '-'}`,
    priority: r.reason_json?.risk_level || 'medium',
    confidence: Number(r.reason_json?.confidence || 0),
    status: r.approval_status || r.status,
    createdAt: r.created_at,
    canApprove: String(r.approval_status || '').toLowerCase() === 'pending',
    canReject: String(r.approval_status || '').toLowerCase() === 'pending',
  }), []);

  const mapDraft = useCallback((r) => ({
    id: `draft-${r.id}`,
    refId: r.id,
    type: 'Draf Tiket Preventif',
    source: 'Operasional',
    title: r.summary || 'Draf tiket preventif',
    summary: r.notes || r.summary || '-',
    explanation: `Prediction: ${r.context_json?.prediction_type || '-'}`,
    priority: r.suggested_priority || 'medium',
    confidence: Number(r.context_json?.confidence || 0),
    status: r.status || 'draft',
    createdAt: r.created_at,
    canApprove: ['draft', 'reviewed'].includes(String(r.status || '').toLowerCase()),
    canReject: ['draft', 'reviewed'].includes(String(r.status || '').toLowerCase()),
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const headers = getHeaders();
      try {
        const ov = await axios.get(`${API}/ai/control-center/overview`, {
          headers,
          params: { prediction_limit: 200, message_limit: 200, draft_limit: 200, activity_limit: 25 },
        });
        const data = ov?.data?.data || {};
        const clusterRows = Array.isArray(data.cluster_priority_queue) ? data.cluster_priority_queue : [];
        const p = (Array.isArray(data.predictions) ? data.predictions : []).map(mapPred);
        const suppressed = (Array.isArray(data.suppressed_predictions) ? data.suppressed_predictions : []).map(mapPred);
        const activeClusters = Array.isArray(data.incident_clusters) ? data.incident_clusters : [];
        const w = (Array.isArray(data.wa_proactive_messages) ? data.wa_proactive_messages : []).map(mapWa);
        const d = (Array.isArray(data.preventive_ticket_drafts) ? data.preventive_ticket_drafts : []).map(mapDraft);
        setIncidentClusters(activeClusters);
        setClusterPriorityQueue(clusterRows.map(mapClusterCard));
        setClusterSummary(data.cluster_summary || {
          open_clusters: 0,
          acknowledged_clusters: 0,
          critical_clusters: 0,
          high_clusters: 0,
        });
        setPredictions(p);
        setSuppressedPredictions(suppressed);
        setWaDrafts(w);
        setTicketDrafts(d);
        setActivity((Array.isArray(data.ai_activity) ? data.ai_activity : []).map((r) => ({
          id: `act-${r.id}`,
          title: r.decision ? `Keputusan AI: ${r.decision}` : 'Aktivitas AI',
          detail: `${r.detected_intent || '-'} � confidence ${Math.round((Number(r.confidence || 0)) * 100)}%`,
          createdAt: r.created_at,
        })));
      } catch {
        const [predRes, waRes, draftRes, decRes] = await Promise.allSettled([
          axios.get(`${API}/ai/predictions`, { headers, params: { limit: 200 } }),
          axios.get(`${API}/wa/proactive-messages`, { headers, params: { limit: 200 } }),
          axios.get(`${API}/ops/preventive-ticket-drafts`, { headers, params: { limit: 200 } }),
          axios.get(`${API}/whatsapp/ai/decisions`, { headers, params: { limit: 25 } }),
        ]);
        const predRows = predRes.status === 'fulfilled' && Array.isArray(predRes.value?.data) ? predRes.value.data : [];
        const waRows = waRes.status === 'fulfilled' && Array.isArray(waRes.value?.data) ? waRes.value.data : [];
        const draftRows = draftRes.status === 'fulfilled' && Array.isArray(draftRes.value?.data) ? draftRes.value.data : [];
        const decRows = decRes.status === 'fulfilled' && Array.isArray(decRes.value?.data) ? decRes.value.data : [];

        setIncidentClusters([]);
        setClusterPriorityQueue([]);
        setClusterSummary({
          open_clusters: 0,
          acknowledged_clusters: 0,
          critical_clusters: 0,
          high_clusters: 0,
        });
        setPredictions(predRows.map(mapPred));
        setSuppressedPredictions([]);
        setWaDrafts(waRows.map(mapWa));
        setTicketDrafts(draftRows.map(mapDraft));
        setActivity(decRows.map((r) => ({
          id: `act-${r.id}`,
          title: r.decision ? `Keputusan AI: ${r.decision}` : 'Aktivitas AI',
          detail: `${r.detected_intent || '-'} � confidence ${Math.round((Number(r.confidence || 0)) * 100)}%`,
          createdAt: r.created_at,
        })));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Gagal memuat AI Control Center');
    } finally {
      setLoading(false);
    }
  }, [getHeaders, mapClusterCard, mapDraft, mapPred, mapWa]);

  useEffect(() => { load(); }, [load]);

  const allItems = useMemo(() => [...predictions, ...waDrafts, ...ticketDrafts], [predictions, waDrafts, ticketDrafts]);
  const approvalItems = useMemo(() => allItems.filter((i) => i.canApprove || i.canReject), [allItems]);
  const nonClusterPriority = useMemo(() => {
    const score = { critical: 4, high: 3, medium: 2, low: 1 };
    return [...allItems].sort((a, b) => (score[b.priority] || 1) - (score[a.priority] || 1) || b.confidence - a.confidence);
  }, [allItems]);
  const priorityQueue = useMemo(() => {
    const clusterCards = Array.isArray(clusterPriorityQueue) ? clusterPriorityQueue : [];
    return [...clusterCards, ...nonClusterPriority].slice(0, 8);
  }, [clusterPriorityQueue, nonClusterPriority]);
  const filteredApprovals = useMemo(() => approvalItems.filter((i) => {
    if (itemType !== 'all' && i.type !== itemType) return false;
    if (status !== 'all' && String(i.status).toLowerCase() !== status) return false;
    if (priority !== 'all' && String(i.priority).toLowerCase() !== priority) return false;
    return true;
  }), [approvalItems, itemType, status, priority]);

  const exportApprovalCsv = useCallback(() => {
    const rows = [
      ['judul', 'tipe', 'source', 'prioritas', 'status', 'confidence', 'dibuat'],
      ...filteredApprovals.map((item) => [
        `"${String(item.title || '-').replaceAll('"', '""')}"`,
        `"${String(item.type || '-').replaceAll('"', '""')}"`,
        `"${String(item.source || '-').replaceAll('"', '""')}"`,
        `"${String(item.priority || '-').replaceAll('"', '""')}"`,
        `"${String(item.status || '-').replaceAll('"', '""')}"`,
        `${Math.round((Number(item.confidence || 0)) * 100)}%`,
        `"${fmt(item.createdAt)}"`,
      ]),
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 19).replaceAll(':', '-');
    a.href = url;
    a.download = `ai-approval-inbox-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [filteredApprovals]);

  const runAction = useCallback(async (item, action) => {
    try {
      const headers = getHeaders();
      if (item.type === 'Draf WA Proaktif') {
        if (action === 'approve') await axios.post(`${API}/wa/proactive-messages/${item.refId}/approve`, {}, { headers });
        if (action === 'reject') await axios.post(`${API}/wa/proactive-messages/${item.refId}/reject`, { reason: 'Ditolak operator' }, { headers });
      }
      if (item.type === 'Draf Tiket Preventif') {
        if (action === 'approve') await axios.post(`${API}/ops/preventive-ticket-drafts/${item.refId}/convert`, {}, { headers });
        if (action === 'reject') await axios.post(`${API}/ops/preventive-ticket-drafts/${item.refId}/reject`, { reason: 'Ditolak operator' }, { headers });
      }
      await load();
      setDrawer(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Aksi gagal');
    }
  }, [getHeaders, load]);

  const openSource = useCallback((item) => {
    if (!item) return;
    if (item.type === 'Draf WA Proaktif') {
      navigate(`/dadu/whatsapp-live?proactive_id=${encodeURIComponent(item.refId)}`);
      return;
    }
    if (item.type === 'Draf Tiket Preventif') {
      navigate(`/dadu/penanganan?preventive_draft_id=${encodeURIComponent(item.refId)}`);
      return;
    }
    if (item.type === 'Prediksi AI') {
      navigate(`/investor?ai_prediction_id=${encodeURIComponent(item.refId)}`);
      return;
    }
    if (item.type === 'Incident Cluster') {
      navigate(`/dadu/ai-clusters/${encodeURIComponent(item.refId)}`);
      return;
    }
    navigate('/dadu');
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const proactiveId = params.get('proactive_id');
    const preventiveId = params.get('preventive_draft_id');
    const predictionId = params.get('ai_prediction_id');
    const clusterId = params.get('cluster_id');
    if (proactiveId) setSelectedItemId(`wa-${proactiveId}`);
    if (preventiveId) setSelectedItemId(`draft-${preventiveId}`);
    if (predictionId) setSelectedItemId(`pred-${predictionId}`);
    if (clusterId) setSelectedItemId(`cluster-${clusterId}`);
  }, [location.search]);

  useEffect(() => {
    if (!selectedItemId || allItems.length === 0) return;
    const matched = allItems.find((item) => item.id === selectedItemId);
    if (matched) {
      setDrawer(matched);
      setShowFilters(true);
    }
  }, [allItems, selectedItemId]);

  return (
    <div className="relative min-h-screen bg-[#050a14]">
      {loading ? <StaffPageLoadingOverlay text="Memuat AI Control Center..." /> : null}
      <AppShell>
      <PageContainer>
        <StaffPageHeader
          eyebrow="AI OPS"
          title="AI Control Center"
          subtitle="Ringkasan keputusan, prediksi, persetujuan, dan aktivitas AI."
          actions={(
            <>
              <Badge cls="border-cyan-500/40 bg-cyan-500/15 text-cyan-200">AI Status Aktif</Badge>
              <Button variant="outline" className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={() => setShowFilters((v) => !v)}>
                <Filter className="mr-2 h-4 w-4" />Filter
              </Button>
              <Button variant="outline" className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={load}>
                <RefreshCw className="mr-2 h-4 w-4" />Muat Ulang
              </Button>
              <Button variant="outline" className="border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={exportApprovalCsv}>
                Ekspor CSV
              </Button>
            </>
          )}
        />

        {showFilters ? (
          <div className="mt-4 grid gap-2 rounded-2xl border border-white/10 bg-[#0b1222] p-3 md:grid-cols-3">
            <select className="rounded-lg border border-white/20 bg-[#07101d] px-2 py-2 text-sm text-white" value={itemType} onChange={(e) => setItemType(e.target.value)}>
              <option value="all">Semua Tipe</option><option value="Prediksi AI">Prediksi AI</option><option value="Draf WA Proaktif">Draf WA Proaktif</option><option value="Draf Tiket Preventif">Draf Tiket Preventif</option>
            </select>
            <select className="rounded-lg border border-white/20 bg-[#07101d] px-2 py-2 text-sm text-white" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">Semua Status</option><option value="pending">Menunggu</option><option value="pending_approval">Menunggu Persetujuan</option><option value="draft">Draf</option><option value="approved">Disetujui</option><option value="rejected">Ditolak</option>
            </select>
            <select className="rounded-lg border border-white/20 bg-[#07101d] px-2 py-2 text-sm text-white" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="all">Semua Prioritas</option><option value="critical">Kritis</option><option value="high">Tinggi</option><option value="medium">Sedang</option><option value="low">Rendah</option>
            </select>
          </div>
        ) : null}

        {error ? (
          <div className="mt-4 rounded-xl border border-red-500/45 bg-red-500/10 p-3 text-red-200 text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />{error}
          </div>
        ) : null}

        <section className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <StatCard label="Prediksi Aktif" value={predictions.length} />
          <StatCard label="Cluster Aktif" value={incidentClusters.length} />
          <StatCard label="Persetujuan Tertunda" value={approvalItems.length} />
          <StatCard label="Draf WA Proaktif" value={waDrafts.length} />
          <StatCard label="Draf Tiket Preventif" value={ticketDrafts.length} />
          <StatCard label="Prediksi Tersuppress" value={suppressedPredictions.length} />
        </section>

        <section className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[1fr,320px]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-white/10 bg-[#081121] p-4">
              <h2 className="mb-3 text-base font-semibold text-white">Antrean Prioritas</h2>
              {priorityQueue.length === 0 ? <EmptyState title="Tidak ada item prioritas" subtitle="Belum ada item yang perlu ditinjau segera." /> : (
                <div className="space-y-3">
                  {priorityQueue.map((item) => (
                    <div key={item.id} className={`rounded-xl border bg-[#0b1222] p-3 ${selectedItemId === item.id ? 'border-cyan-400/60 ring-1 ring-cyan-400/40' : 'border-white/10'}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge cls={priorityClass(item.priority)}>{item.priority}</Badge>
                        <Badge cls={statusClass(item.status)}>{item.status}</Badge>
                        <Badge cls="border-cyan-500/35 bg-cyan-500/10 text-cyan-200">{item.source}</Badge>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-white">{item.title}</p>
                      <p className="text-xs text-slate-300">{item.summary}</p>
                      {item.isCluster ? (
                        <div className="mt-2 rounded-lg border border-white/10 bg-[#081121] p-2 text-[11px] text-slate-300">
                          <p>Pelanggan terdampak: <span className="text-white">{item.affectedCustomersCount}</span></p>
                          <p>Event sumber: <span className="text-white">{item.sourceEventsCount}</span></p>
                          <p>
                            Breakdown: tiket {item.memberBreakdown?.tickets || 0}, WA {item.memberBreakdown?.wa_complaints || 0}, prediksi {item.memberBreakdown?.predictions || 0}
                          </p>
                        </div>
                      ) : null}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" className="h-8 border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={() => setDrawer(item)}>
                          <Eye className="mr-1 h-3.5 w-3.5" />Lihat Detail
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 border-white/20 text-slate-100" onClick={() => openSource(item)}>Buka Sumber</Button>
                        {item.canApprove ? <Button size="sm" variant="outline" className="h-8 border-emerald-500/40 bg-emerald-500/10 text-emerald-100" onClick={() => runAction(item, 'approve')}>Setujui</Button> : null}
                        {item.canReject ? <Button size="sm" variant="outline" className="h-8 border-red-500/40 bg-red-500/10 text-red-100" onClick={() => runAction(item, 'reject')}>Tolak</Button> : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#081121] p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-white">Approval Inbox</h2>
                <Button size="sm" variant="outline" className="h-8 border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={exportApprovalCsv}>
                  Ekspor CSV
                </Button>
              </div>
              {filteredApprovals.length === 0 ? <EmptyState title="Tidak ada persetujuan" subtitle="Inbox persetujuan sedang kosong." /> : (
                <div className="overflow-hidden rounded-xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0a1120] text-slate-300">
                      <tr><th className="p-3 text-left">Judul</th><th className="p-3 text-left">Tipe</th><th className="p-3 text-left">Prioritas</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Dibuat</th><th className="p-3 text-right">Aksi</th></tr>
                    </thead>
                    <tbody>
                      {filteredApprovals.map((item) => (
                        <tr key={item.id} className={`border-t bg-[#0b1222] ${selectedItemId === item.id ? 'border-cyan-500/45' : 'border-white/10'}`}>
                          <td className="p-3 text-white">{item.title}</td>
                          <td className="p-3 text-slate-300">{item.type}</td>
                          <td className="p-3"><Badge cls={priorityClass(item.priority)}>{item.priority}</Badge></td>
                          <td className="p-3"><Badge cls={statusClass(item.status)}>{item.status}</Badge></td>
                          <td className="p-3 text-slate-400">{fmt(item.createdAt)}</td>
                          <td className="p-3">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" className="h-8 border-cyan-400/35 bg-cyan-500/10 text-cyan-100" onClick={() => setDrawer(item)}>Detail</Button>
                              <Button size="sm" variant="outline" className="h-8 border-white/20 text-slate-100" onClick={() => openSource(item)}>Sumber</Button>
                              {item.canApprove ? <Button size="sm" variant="outline" className="h-8 border-emerald-500/40 bg-emerald-500/10 text-emerald-100" onClick={() => runAction(item, 'approve')}>Setujui</Button> : null}
                              {item.canReject ? <Button size="sm" variant="outline" className="h-8 border-red-500/40 bg-red-500/10 text-red-100" onClick={() => runAction(item, 'reject')}>Tolak</Button> : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#081121] p-4">
              <h2 className="mb-3 text-base font-semibold text-white">Aktivitas AI</h2>
              {activity.length === 0 ? <EmptyState title="Belum ada aktivitas AI" subtitle="Aktivitas terbaru akan tampil di sini." /> : (
                <div className="space-y-2">
                  {activity.slice(0, 15).map((ev) => (
                    <div key={ev.id} className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#0b1222] p-3">
                      <Activity className="mt-0.5 h-4 w-4 text-cyan-300" />
                      <div>
                        <p className="text-sm font-medium text-white">{ev.title}</p>
                        <p className="text-xs text-slate-300">{ev.detail}</p>
                        <p className="text-[11px] text-slate-400">{fmt(ev.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-white/10 bg-[#0b1222] p-4">
              <h3 className="text-sm font-semibold text-white">Kesehatan AI</h3>
              <div className="mt-3 space-y-2 text-xs text-slate-300">
                <div className="flex items-center justify-between"><span>Item low-confidence</span><span>{allItems.filter((i) => i.confidence < 0.45).length}</span></div>
                <div className="flex items-center justify-between"><span>Gagal/ditolak</span><span>{allItems.filter((i) => ['failed', 'rejected', 'blocked', 'cancelled'].includes(String(i.status).toLowerCase())).length}</span></div>
                <div className="flex items-center justify-between"><span>Menunggu persetujuan</span><span>{approvalItems.length}</span></div>
                <div className="flex items-center justify-between"><span>Prediksi berisiko tinggi</span><span>{predictions.filter((i) => ['high', 'critical'].includes(String(i.priority).toLowerCase())).length}</span></div>
                <div className="flex items-center justify-between"><span>Cluster kritis/tinggi</span><span>{(Number(clusterSummary.critical_clusters || 0) + Number(clusterSummary.high_clusters || 0))}</span></div>
                <div className="mt-2 rounded-lg border border-cyan-500/35 bg-cyan-500/10 p-2 text-cyan-100">Mode AI: review-first</div>
              </div>
            </div>
          </aside>
        </section>
                  </PageContainer>
    </AppShell>

      {drawer ? (
        <div className="fixed inset-0 z-[80] flex">
          <button type="button" className="flex-1 bg-black/65" onClick={() => setDrawer(null)} />
          <div className="w-full max-w-xl border-l border-white/10 bg-[#07101d] p-5">
            <h3 className="text-lg font-bold text-white">{drawer.title}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge cls={priorityClass(drawer.priority)}>Prioritas {drawer.priority}</Badge>
              <Badge cls={statusClass(drawer.status)}>{drawer.status}</Badge>
              <Badge cls="border-cyan-500/35 bg-cyan-500/10 text-cyan-200">{drawer.source}</Badge>
            </div>
            <div className="mt-4 rounded-xl border border-white/10 bg-[#0b1222] p-4">
              <p className="text-xs text-slate-400">Ringkasan</p>
              <p className="text-sm text-white">{drawer.summary}</p>
              <p className="mt-3 text-xs text-slate-400">Penjelasan</p>
              <p className="text-sm text-slate-200">{drawer.explanation}</p>
              <p className="mt-3 text-xs text-slate-400">Confidence</p>
              <p className="text-sm text-white">{Math.round((Number(drawer.confidence || 0)) * 100)}%</p>
              <p className="mt-3 text-xs text-slate-400">Dibuat</p>
              <p className="text-sm text-slate-200">{fmt(drawer.createdAt)}</p>
              {drawer.isCluster && Array.isArray(drawer.recommendedActions) && drawer.recommendedActions.length > 0 ? (
                <>
                  <p className="mt-3 text-xs text-slate-400">Rekomendasi Tindakan</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-slate-200">
                    {drawer.recommendedActions.map((row) => <li key={row}>{row}</li>)}
                  </ul>
                </>
              ) : null}
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" className="border-white/20 text-slate-100" onClick={() => openSource(drawer)}>Buka Sumber</Button>
              {drawer.canApprove ? <Button className="bg-emerald-500 hover:bg-emerald-400" onClick={() => runAction(drawer, 'approve')}>Approve</Button> : null}
              {drawer.canReject ? <Button className="bg-red-500 hover:bg-red-400" onClick={() => runAction(drawer, 'reject')}>Reject</Button> : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminAIControlCenter;





